import { FileObject, FilePurpose, FileType } from '@citruscamps/citrus-client'
import { faTimes } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useEffect, useState } from 'react'
import { Dropzone, DropzoneFile } from '../../Dropzone/Dropzone'
import { LoadingButton } from '../../LoadingButton/LoadingButton'
import { MediaLibraryView } from './MediaLibraryView'
import { MediaStockView } from './MediaStockView'

interface IProps {
  value?: FileObject
  show: boolean
  tab?: 'library' | 'stock' | 'upload'
  programId: string
  accept?: FileType[]
  purpose?: FilePurpose
  dropzoneMessage?: string
  onClose: () => Promise<void> | void
  onSubmit: (files?: DropzoneFile, openCrop?: boolean) => Promise<void> | void
}

export const MediaSelectModal = ({
  value,
  show,
  programId,
  purpose,
  accept,
  tab: defaultTab = 'library',
  dropzoneMessage,
  onClose,
  onSubmit: handleSubmit,
}: IProps) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [selected, setSelected] = useState<FileObject | undefined>(value)
  const [tab, setTab] = useState<'library' | 'stock' | 'upload'>(defaultTab)

  useEffect(() => {
    if (tab !== defaultTab) {
      setTab(defaultTab)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTab])

  return (
    <>
      {show && (
        <>
          <div className="modal fade show fade-in overlay d-flex justify-content-center">
            <div
              className="slide-in-blurred-top col-12 col-sm-10 col-md-8 col-lg-6 col-xl-5 mx-0 my-auto"
              style={{ maxWidth: 'unset' }}
            >
              <div className="modal-content">
                <div className="modal-header d-flex border-0 pb-0">
                  <button type="button" className="btn text-body ms-auto" onClick={onClose}>
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
                <div className="modal-body pt-0">
                  <ul className="nav nav-tabs nav-overflow">
                    <li className="nav-item">
                      <button
                        type="button"
                        className={['nav-link', tab === 'library' ? 'active' : '']
                          .filter(Boolean)
                          .join(' ')}
                        onClick={() => setTab('library')}
                      >
                        My library
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        type="button"
                        className={['nav-link', tab === 'stock' ? 'active' : '']
                          .filter(Boolean)
                          .join(' ')}
                        onClick={() => setTab('stock')}
                      >
                        Free photos
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        type="button"
                        className={['nav-link', tab === 'upload' ? 'active' : '']
                          .filter(Boolean)
                          .join(' ')}
                        onClick={() => setTab('upload')}
                      >
                        Upload file
                      </button>
                    </li>
                  </ul>
                  <div className="img-selector-container">
                    {(() => {
                      switch (tab) {
                        case 'upload':
                          return (
                            <Dropzone
                              icon="image"
                              children={dropzoneMessage}
                              files={value ? [value] : []}
                              maxFileCount={1}
                              uploadOnDrop={true}
                              purpose={purpose}
                              onUpload={async (files): Promise<void> => {
                                setIsSubmitting(true)
                                try {
                                  await handleSubmit?.(files[0], true)
                                } catch {}
                                setIsSubmitting(false)
                              }}
                              style={{ height: 350, margin: '1rem' }}
                              accept={accept || 'image/*'}
                              context={programId}
                            />
                          )
                        case 'library':
                          return (
                            <MediaLibraryView
                              value={selected}
                              programId={programId}
                              filetypes={accept}
                              purposes={purpose ? [purpose] : purpose}
                              onSelect={(item?: FileObject) => setSelected(item)}
                            />
                          )
                        case 'stock':
                          return (
                            <MediaStockView
                              value={selected}
                              programId={programId}
                              purpose={purpose}
                              onSelect={async (item?: FileObject) => {
                                setSelected(item)
                                await handleSubmit(item, true)
                              }}
                            />
                          )
                      }
                    })()}
                  </div>
                </div>
                {tab === 'library' && (
                  <div className="modal-footer d-flex justify-content-between border-0">
                    <button
                      type="button"
                      className="btn btn-link btn-block d-md-inline-block w-md-auto order-md-1 order-3 mt-2"
                      onClick={() => onClose()}
                    >
                      Cancel
                    </button>
                    <LoadingButton
                      type="button"
                      className="btn btn-link btn-block d-md-inline-block w-md-auto order-2 ms-auto"
                      loading={isSubmitting}
                      onClick={async () => {
                        setIsSubmitting(true)
                        try {
                          await handleSubmit?.(selected, true)
                        } catch {}
                        setIsSubmitting(false)
                      }}
                    >
                      Select & Crop
                    </LoadingButton>
                    <LoadingButton
                      type="button"
                      className="btn btn-primary btn-block d-md-inline-block w-md-auto order-md-3 order-1"
                      loading={isSubmitting}
                      onClick={async () => {
                        setIsSubmitting(true)
                        try {
                          await handleSubmit?.(selected)
                        } catch {}
                        setIsSubmitting(false)
                      }}
                    >
                      Select
                    </LoadingButton>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </>
  )
}
